import { router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { t } from '@/shared/lib/i18n'
import { adminInstrumentTagPath, adminInstrumentTagsPath } from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { INSTRUMENT_TAG_MAX_NAME_LENGTH } from '@/shared/constants/instrumentTag'

interface Props {
  tag: { id: number; name: string }
  errors?: { base?: string[] }
  [key: string]: unknown
}

const InstrumentTagEditPage = () => {
  const { tag, errors: pageErrors } = usePage<Props>().props

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.instrumentTags'), href: adminInstrumentTagsPath() },
    { label: t('admin.instrumentTags.editTitle') },
  ])

  const form = useForm({ name: tag.name })

  const baseErrors = pageErrors?.base ?? []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ tag: data }))
    form.patch(adminInstrumentTagPath(tag.id))
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-6">{t('admin.instrumentTags.editTitle')}</h1>

        {baseErrors.length > 0 && (
          <div className="mb-4 rounded border border-destructive bg-destructive/10 px-4 py-3">
            {baseErrors.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="space-y-1.5">
            <Label htmlFor="name">{t('admin.instrumentTags.nameField')} *</Label>
            <Input
              id="name"
              value={form.data.name}
              onChange={(e) => form.setData('name', e.target.value)}
              maxLength={INSTRUMENT_TAG_MAX_NAME_LENGTH}
              required
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing}>
              {t('admin.instrumentTags.submitUpdate')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => router.visit(adminInstrumentTagsPath())}
            >
              {t('admin.instrumentTags.cancelButton')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InstrumentTagEditPage
